import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as aiPromptService from '../services/aiPromptService';
import { createAIJob, streamAIJob } from '../services/aiJobService';
import FeaturePanel from './FeaturePanel';
import { lexicalToHTML } from '../utils/lexicalUtils';
import { updateFeature } from '../services/featureService';
import './ImprovePromptView.css';

const SYSTEM_PROMPT_ID = 'ac8a37de-82de-4a75-a109-376591961259';

/**
 * Render prompt template with feature data
 * Supports both {{feature.field}} and [features.field] syntax
 * Matches server-side renderPromptTemplate function
 */
const renderPromptTemplate = (template, feature) => {
  if (!template || !feature) return template;
  
  let rendered = template;

  // Replace [features.field] syntax - e.g., [features.name], [features.description]
  // This supports any field from the feature object
  rendered = rendered.replace(/\[features\.(\w+)\]/g, (match, fieldName) => {
    // Special handling for details field - convert to HTML
    if (fieldName === 'details') {
      if (feature?.details_html !== undefined && feature?.details_html !== null && feature.details_html !== '') {
        return feature.details_html;
      } else if (feature?.details) {
        return lexicalToHTML(feature.details);
      }
      return '';
    }
    
    const value = feature?.[fieldName];
    if (value === null || value === undefined) {
      return ''; // Return empty string if field doesn't exist
    }
    // Handle JSON fields by stringifying them
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  });

  // Replace legacy {{feature.field}} syntax for backward compatibility
  rendered = rendered.replace(/\{\{feature\.name\}\}/g, feature?.name || '');
  rendered = rendered.replace(/\{\{feature\.description\}\}/g, feature?.description || '');
  // Use details_html if available, otherwise convert details from Lexical to HTML on the fly
  let detailsValue = '';
  if (feature?.details_html !== undefined && feature?.details_html !== null && feature.details_html !== '') {
    detailsValue = feature.details_html;
  } else if (feature?.details) {
    // Convert Lexical JSON to HTML if details_html is not available
    detailsValue = lexicalToHTML(feature.details);
  }
  rendered = rendered.replace(/\{\{feature\.details\}\}/g, detailsValue);
  rendered = rendered.replace(/\{\{feature\.status\}\}/g, feature?.status || '');
  rendered = rendered.replace(/\{\{feature\.ai_prompt\}\}/g, feature?.ai_prompt || '');
  
  // Also handle [features.details_html] syntax - convert on the fly if needed
  let detailsHtmlValue = '';
  if (feature?.details_html !== undefined && feature?.details_html !== null && feature.details_html !== '') {
    detailsHtmlValue = feature.details_html;
  } else if (feature?.details) {
    detailsHtmlValue = lexicalToHTML(feature.details);
  }
  rendered = rendered.replace(/\[features\.details_html\]/g, detailsHtmlValue);

  return rendered;
};

const ImprovePromptView = ({ feature, featureGroupId, onClose, onSave, onUpdateFeature, onReady }) => {
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(SYSTEM_PROMPT_ID);
  const [selectedPrompt, setSelectedPrompt] = useState(null); // Store the full selected prompt object
  const [renderedPromptBody, setRenderedPromptBody] = useState(''); // Store the rendered prompt with values
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [output, setOutput] = useState('');
  const [actions, setActions] = useState(null);
  const [error, setError] = useState(null);
  const outputRef = useRef(null);

  useEffect(() => {
    // Auto-scroll output to bottom
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const loadPrompts = useCallback(async () => {
    try {
      // Use namespace import to avoid potential bundling issues
      if (!aiPromptService.getSystemAIPrompts) {
        console.error('getSystemAIPrompts is not available in aiPromptService');
        setError('Failed to load prompts: Service function not available');
        if (onReady) onReady();
        return;
      }
      const data = await aiPromptService.getSystemAIPrompts();
      setPrompts(data);
      // Set default to the specified system prompt if available
      const defaultPrompt = data.find(p => p.id === SYSTEM_PROMPT_ID);
      if (defaultPrompt) {
        setSelectedPromptId(SYSTEM_PROMPT_ID);
        setSelectedPrompt(defaultPrompt);
      } else if (data.length > 0) {
        setSelectedPromptId(data[0].id);
        setSelectedPrompt(data[0]);
      }
      // Notify parent that component is ready
      if (onReady) onReady();
    } catch (err) {
      console.error('Error loading prompts:', err);
      setError('Failed to load prompts');
      if (onReady) onReady();
    }
  }, [onReady]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  // Update selected prompt body when selection changes and render it with feature values
  useEffect(() => {
    if (selectedPromptId && prompts.length > 0) {
      const prompt = prompts.find(p => p.id === selectedPromptId);
      if (prompt) {
        setSelectedPrompt(prompt);
        const rawBody = prompt.body || '';
        const rendered = renderPromptTemplate(rawBody, feature);
        setRenderedPromptBody(rendered);
      } else {
        setSelectedPrompt(null);
        setRenderedPromptBody('');
      }
    } else {
      setSelectedPrompt(null);
      setRenderedPromptBody('');
    }
  }, [selectedPromptId, prompts, feature]);

  const handleGo = async (reloadPrompt = false) => {
    if (!selectedPromptId || !feature?.id) {
      setError('Please select a prompt');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Reload prompt from database if requested (e.g., from refresh button)
      if (reloadPrompt) {
        try {
          const reloadedPrompt = await aiPromptService.getSystemAIPromptById(selectedPromptId);
          if (reloadedPrompt) {
            setSelectedPrompt(reloadedPrompt);
            const rawBody = reloadedPrompt.body || '';
            const rendered = renderPromptTemplate(rawBody, feature);
            setRenderedPromptBody(rendered);
          }
        } catch (reloadError) {
          console.error('Error reloading prompt:', reloadError);
          // Continue with existing prompt if reload fails
        }
      }
      
      setOutput(''); // Clear previous output
      setActions(null);
      setStreaming(true);

      // Create AI job
      const job = await createAIJob(feature.id, selectedPromptId, true);

      // Start streaming
      await streamAIJob(job.job_id, {
        onToken: (text) => {
          setOutput(prev => prev + text);
          // Ensure streaming is true while receiving tokens
          setStreaming(true);
        },
        onStatus: (status) => {
          console.log('Status update:', status);
        },
        onFinal: (data) => {
          // Parse final result
          const resultText = data.result_text || '';
          setOutput(resultText);
          
          // Stop streaming and loading immediately
          setStreaming(false);
          setLoading(false);

          // Try to parse JSON actions
          try {
            const resultJson = data.result_json || JSON.parse(resultText);
            if (resultJson && resultJson.action) {
              setActions(resultJson);
            }
          } catch (e) {
            // Not JSON or no action, ignore
          }
        },
        onError: (error) => {
          setError(error.message || 'Streaming error');
          setStreaming(false);
          setLoading(false);
        },
      });
    } catch (err) {
      console.error('Error creating job:', err);
      setError(err.message || 'Failed to create AI job');
      setLoading(false);
      setStreaming(false);
    }
  };

  const handleAction = (actionType, actionData) => {
    // Handle action button clicks
    console.log('Action:', actionType, actionData);
    // This will be implemented based on what actions are returned
    // For now, just log it
  };

  // Handle copying AI response to a feature field
  const handleCopyToField = async (fieldName) => {
    if (!output || !feature?.id) return;

    try {
      // Get the current feature data
      const currentFeature = { ...feature };
      
      // Prepare update data based on field name
      const updateData = {};
      
      switch (fieldName) {
        case 'name':
          updateData.name = output.trim();
          break;
        case 'description':
          updateData.description = output.trim();
          break;
        case 'details':
          // For details, we need to convert the plain text output to Lexical JSON
          // Store it as a simple paragraph in Lexical format
          const lexicalJson = {
            root: {
              children: [{
                children: [{
                  detail: 0,
                  format: 0,
                  mode: 'normal',
                  style: '',
                  text: output.trim(),
                  type: 'text',
                  version: 1
                }],
                direction: 'ltr',
                format: '',
                indent: 0,
                type: 'paragraph',
                version: 1
              }],
              direction: 'ltr',
              format: '',
              indent: 0,
              type: 'root',
              version: 1
            }
          };
          updateData.details = JSON.stringify(lexicalJson);
          // Also update details_html
          updateData.details_html = lexicalToHTML(JSON.stringify(lexicalJson));
          break;
        case 'ai_prompt':
          updateData.ai_prompt = output.trim();
          break;
        default:
          console.error('Unknown field:', fieldName);
          return;
      }

      // Update the feature using the service directly
      await updateFeature(feature.id, {
        ...currentFeature,
        ...updateData,
        originalUpdatedAt: currentFeature.updated_at
      });
      
      // Update the feature without closing Improve Feature mode
      const updatedFeature = { ...currentFeature, ...updateData };
      if (onUpdateFeature) {
        await onUpdateFeature(updatedFeature);
      } else if (onSave) {
        // Fallback to onSave if onUpdateFeature is not provided
        await onSave(updatedFeature, [], []);
      }
      
      // Show success feedback
      alert(`Successfully copied AI response to ${getFieldLabel(fieldName)}`);
    } catch (err) {
      console.error('Error copying to field:', err);
      alert(`Failed to copy AI response to ${getFieldLabel(fieldName)}. Please try again.`);
    }
  };

  // Get field label for display
  const getFieldLabel = (fieldName) => {
    const labels = {
      name: 'Name',
      description: 'Description',
      details: 'Details',
      ai_prompt: 'AI Prompt'
    };
    return labels[fieldName] || fieldName;
  };

  return (
    <div className="improve-prompt-view">
      <div className="improve-prompt-header">
        <h2>Improve Feature</h2>
        <button className="close-button" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="improve-prompt-content">
        {/* Left side: Chat interface */}
        <div className="improve-prompt-chat">
          <div className="chat-header">
            <label htmlFor="prompt-select" className="prompt-select-label">Select Prompt</label>
            <select
              id="prompt-select"
              value={selectedPromptId || ''}
              onChange={(e) => setSelectedPromptId(e.target.value)}
              className="prompt-select"
              disabled={loading || streaming}
            >
              <option value="">Select a prompt...</option>
              {prompts.map(prompt => (
                <option key={prompt.id} value={prompt.id}>
                  {prompt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="chat-output" ref={outputRef}>
            {/* Show rendered prompt immediately when selected (yellow bubble from left) */}
            {renderedPromptBody && (
              <>
                <div className="prompt-bubble prompt-bubble-left">
                  <div className="prompt-bubble-content">
                    {renderedPromptBody}
                    {/* Refresh button - show after AI response is generated */}
                    {output && !loading && !streaming && (
                      <button
                        className="refresh-prompt-button"
                        onClick={() => handleGo(true)}
                        disabled={loading || streaming}
                        title="Re-run this prompt (reloads prompt from database)"
                      >
                        <i className="fas fa-redo"></i>
                      </button>
                    )}
                  </div>
                </div>
                {/* Run Prompt button under the yellow prompt - hide when running or after output */}
                {!loading && !streaming && !output && (
                  <div className="run-prompt-button-container">
                    <button
                      className="run-prompt-button"
                      onClick={handleGo}
                      disabled={!selectedPromptId || loading || streaming}
                    >
                      <i className="fas fa-play"></i> Run Prompt
                    </button>
                  </div>
                )}
              </>
            )}
            
            {/* Show streaming indicator only when actually streaming and no output yet */}
            {streaming && !output && (
              <div className="prompt-bubble prompt-bubble-right">
                <div className="prompt-bubble-content streaming-indicator">
                  <i className="fas fa-spinner fa-spin"></i> Thinking...
                </div>
              </div>
            )}
            
            {/* Show AI response in blue bubble (right side) */}
            {output && (
              <>
                <div className="prompt-bubble prompt-bubble-right">
                  <div className="prompt-bubble-content">{output}</div>
                </div>
                {/* Show copy buttons for updatable fields */}
                {selectedPrompt?.updatable_fields && selectedPrompt.updatable_fields.length > 0 && (
                  <div className="copy-to-field-buttons">
                    {selectedPrompt.updatable_fields.map((fieldName) => (
                      <button
                        key={fieldName}
                        className="copy-to-field-button"
                        onClick={() => handleCopyToField(fieldName)}
                        title={`Copy AI response to ${getFieldLabel(fieldName)}`}
                      >
                        <i className="fas fa-copy"></i> Copy the value to {getFieldLabel(fieldName)}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {/* Placeholder when nothing is selected */}
            {!renderedPromptBody && !output && !streaming && (
              <div className="output-placeholder">
                Select a prompt to start improving your feature.
              </div>
            )}
          </div>

          {actions && actions.action && (
            <div className="action-buttons">
              <h4>Actions:</h4>
              {actions.action === 'update_description' && (
                <button
                  className="action-button"
                  onClick={() => handleAction('update_description', actions)}
                >
                  Update Description
                </button>
              )}
              {actions.action === 'update_details' && (
                <button
                  className="action-button"
                  onClick={() => handleAction('update_details', actions)}
                >
                  Update Details
                </button>
              )}
              {/* Add more action types as needed */}
            </div>
          )}

          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}
        </div>

        {/* Right side: Edit Feature Panel */}
        <div className="improve-prompt-panel">
          <FeaturePanel
            isOpen={true}
            mode="edit"
            feature={feature}
            parentFeature={null}
            allFeatures={[]}
            featureGroupId={featureGroupId}
            onSave={onSave}
            onClose={onClose}
            onCancelToView={onClose}
            userRole="Owner"
            onEdit={() => {}}
            inline={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ImprovePromptView;

